import { Button, Grid, Paper, Tooltip } from "@mui/material"
import { formatStringToFloatDecimalCase } from "src/utils/format-decimal"
import ShowChartRoundedIcon from '@mui/icons-material/ShowChartRounded'
import { DataGrid } from "@mui/x-data-grid"
import dayjs from "dayjs"
import React, { useEffect, useState } from "react"
import PriceService from "src/services/price-service"
import { DATE_FORMAT_SHOW } from "src/utils/constants"
import { localeBRDataGridText } from "src/utils/data-grid-local-text"
import MultiAxisChartRangeDateModalComponent from "src/components/chart/multiaxis-chart-range-date-modal"
import AlertMessage from "src/components/alert-message"
import PropTypes from "prop-types"

const PriceTablePage = ({ date, loading, setLoading, setExportFileCsv }) => {
    const priceService = new PriceService()
    const [priceList, setPriceList] = useState([])
    const [isChartOpen, setIsChartOpen] = useState(false)
    const [selectedRowData, setSelectedRowData] = useState(null)
    const [alertMessageState, setAlertMessageState] = useState({
        open: false,
        message: "",
        type: "info"
    })
    const [chart, setChart] = useState({
        title: "Gráfico de PU",
        xTitle: "Data Referência",
        yTitle: "PU",
        yRightTitle: "%PUPAR",
        data: []
    })
    const [csvData, setCsvData] = useState([])

    useEffect(() => {
        setExportFileCsv(() => async () => {
            return priceService.exportDataToCsv(date)
        })
        loadPrices(date)
    }, [date])

    const handleOpenChart = (rowData) => {
        setSelectedRowData(rowData)
        setIsChartOpen(true)
    }

    const loadPrices = () => {
        if (date === null) return

        setLoading(true)
        priceService.getPricesv2(date, date)
            .then((response) => {
                setLoading(false)
                const { body } = response
                const { data } = body
                const rows = data.map((obj, index) => ({ ...obj, id: index }))
                setPriceList(rows)
            }).catch(() => {
                setLoading(false)
            })
    }

    const renderCellWithTooltip = (params) => {
        const { value } = params

        return (
            <div title={value}>
                {value}
            </div>
        )
    }

    const formatValue = (params, minimum, maximum) => {
        const { value } = params

        return formatStringToFloatDecimalCase(value, minimum, maximum)
    }

    const columns = [
        {
            field: 'Gráfico',
            width: 60,
            minWidth: 60,
            maxWidth: 60,
            renderCell: (params) => {
                return (
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => handleOpenChart(params.row)}
                        sx={{
                            minWidth: 0,          // Remove a largura mínima padrão
                            width: '35px',        // Define uma largura fixa
                            height: '30px',       // Define uma altura fixa
                            padding: 0
                        }}
                    >
                        <ShowChartRoundedIcon />
                    </Button>
                )
            }
        },
        {
            field: 'DataReferencia',
            headerName: 'Data Referência',
            width: 115,
            type: 'date',
            valueGetter: () => date && dayjs(date).toDate(),
            valueFormatter: () => dayjs(date).format(DATE_FORMAT_SHOW),
        },
        {
            field: 'CETIP',
            headerName: 'CETIP',
            flex: true,
            maxWidth: 120,
            minWidth: 95,
            renderCell: renderCellWithTooltip,
        },
        {
            field: 'tipo_ativo',
            headerName: 'Tipo',
            width: 30,
        },
        {
            field: 'DataVencimento',
            headerName: 'Vencimento',
            width: 115,
            type: 'date',
            valueGetter: (params) => {
                if (!params.value) return null
                return dayjs(params.value).toDate()
            },
            valueFormatter: (params) => {
                if (params.value === "" || params.value == null) {
                    return ""
                }
                const dataDayjs = dayjs(params.value)
                if (!dataDayjs.isValid()) {
                    return ""
                }
                return dataDayjs.format(DATE_FORMAT_SHOW)
            },
        },
        {
            field: 'devedor',
            headerName: 'Devedor',
            minWidth: 110
        },
        {
            field: 'indicador',
            headerName: 'Indicador',
            minWidth: 40,
        },
        {
            field: 'taxa_accrual',
            headerName: 'Taxa ACCRUAL',
            flex: 1,
            minWidth: 70,
            renderCell: (params) => {
                const valueFormated = formatValue(params)
                return (
                    <Tooltip title={valueFormated} placement="bottom-start">
                        <span>
                            {valueFormated}
                        </span>
                    </Tooltip>
                )
            },
        },
        {
            field: 'TaxaMTM',
            headerName: 'Taxa MTM',
            flex: 1,
            minWidth: 70,
            renderCell: (params) => {
                const valueFormated = formatValue(params)
                return (
                    <Tooltip title={valueFormated} placement="bottom-start">
                        <span>
                            {valueFormated}
                        </span>
                    </Tooltip>
                )
            },
        },
        {
            field: 'PU_Inf',
            headerName: 'PU MIN',
            flex: 1,
            minWidth: 70,
            renderCell: (params) => {
                const valueFormated = formatValue(params)
                return (
                    <Tooltip title={valueFormated} placement="bottom-start">
                        <span>
                            {valueFormated}
                        </span>
                    </Tooltip>
                )
            },
        },
        {
            field: 'PU_MTM',
            headerName: 'PU MTM',
            flex: 1,
            minWidth: 70,
            renderCell: (params) => {
                const valueFormated = formatValue(params)
                return (
                    <Tooltip title={valueFormated} placement="bottom-start">
                        <span>
                            {valueFormated}
                        </span>
                    </Tooltip>
                )
            },
        },       
        {
            field: 'SpreadCredito',
            headerName: 'SpreadCredito',
            flex: 1,
            minWidth: 70,
            renderCell: (params) => {
                const valueFormated = formatValue(params)
                return (
                    <Tooltip title={valueFormated} placement="bottom-start">
                        <span>
                            {valueFormated}
                        </span>
                    </Tooltip>
                )
            },
        },
        {
            field: 'PU_Sup',
            headerName: 'PU MAX',
            flex: 1,
            minWidth: 70,
            renderCell: (params) => {
                const valueFormated = formatValue(params)
                return (
                    <Tooltip title={valueFormated} placement="bottom-start">
                        <span>
                            {valueFormated}
                        </span>
                    </Tooltip>
                )
            },
        },
        {
            field: 'PUPAR',
            headerName: 'PUPAR',
            flex: 1,
            minWidth: 70,
            renderCell: (params) => {
                const valueFormated = formatValue(params)
                return (
                    <Tooltip title={valueFormated} placement="bottom-start">
                        <span>
                            {valueFormated}
                        </span>
                    </Tooltip>
                )
            },
        },
        {
            field: 'PercentualPUPAR',
            headerName: '%PUPAR',
            flex: 1,
            minWidth: 70,
            renderCell: (params) => {
                const valueFormated = formatValue(params)
                return (
                    <Tooltip title={valueFormated} placement="bottom-start">
                        <span>
                            {valueFormated}
                        </span>
                    </Tooltip>
                )
            },
        },
        {
            field: 'DurationMTM',
            headerName: 'Duration DU',
            flex: 1,
            minWidth: 70,
            renderCell: (params) => {
                const valueFormated = formatValue(params)
                return (
                    <Tooltip title={valueFormated} placement="bottom-start">
                        <span>
                            {valueFormated}
                        </span>
                    </Tooltip>
                )
            },
        },
        {
            field: 'DurationAno',
            headerName: 'Duration Ano',
            flex: 1,
            minWidth: 70,
            renderCell: (params) => {
                const valueFormated = formatValue(params)
                return (
                    <Tooltip title={valueFormated} placement="bottom-start">
                        <span>
                            {valueFormated}
                        </span>
                    </Tooltip>
                )
            },
        },
        {
            field: 'IndiceLiquidez',
            headerName: 'Índice Liquidez',
            flex: 1,
            type: 'number',
            valueFormatter: (params) => {
                if (params.value === "" || params.value == null || isNaN(Number(params.value))) {
                    return ""
                }
                return params.value
            }
        },
        {
            field: 'DataEventoProximo',
            headerName: 'DataEventoProximo',
            width: 115,
            type: 'date',
            valueGetter: (params) => {
                if (!params.value) return null
                return dayjs(params.value).toDate()
            },
            valueFormatter: (params) => {
                if (params.value === "" || params.value == null) {
                    return ""
                }
                const dataDayjs = dayjs(params.value)
                if (!dataDayjs.isValid()) {
                    return ""
                }
                return dataDayjs.format(DATE_FORMAT_SHOW)
            },
        },
        {
            field: 'ValorProximoEvento',
            headerName: 'ValorProximoEvento',
            flex: 1,
            minWidth: 70,
            renderCell: (params) => {
                if (params.value === "" || params.value == null || isNaN(Number(params.value))) {
                    return ""
                }
                const valueFormated = formatValue(params)
                return (
                    <Tooltip title={valueFormated} placement="bottom-start">
                        <span>
                            {valueFormated}
                        </span>
                    </Tooltip>
                )
            },
        },
        {
            field: 'PremioRecompra',
            headerName: 'PremioRecompra',
            flex: 1,
            minWidth: 70,
            renderCell: (params) => {
                if (params.value === "" || params.value == null || isNaN(Number(params.value))) {
                    return ""
                }
                const valueFormated = formatValue(params)
                return (
                    <Tooltip title={valueFormated} placement="bottom-start">
                        <span>
                            {valueFormated}
                        </span>
                    </Tooltip>
                )
            },
        },
        {
            field: 'SpreadRecompra',
            headerName: 'SpreadRecompra',
            flex: 1,
            minWidth: 70,
            renderCell: (params) => {
                if (params.value === "" || params.value == null || isNaN(Number(params.value))) {
                    return ""
                }
                const valueFormated = formatValue(params)
                return (
                    <Tooltip title={valueFormated} placement="bottom-start">
                        <span>
                            {valueFormated}
                        </span>
                    </Tooltip>
                )
            },
        },
        {
            field: 'PremioaPagar',
            headerName: 'PremioaPagar',
            flex: 1,
            minWidth: 70,
            renderCell: (params) => {
                if (params.value === "" || params.value == null || isNaN(Number(params.value))) {
                    return ""
                }
                const valueFormated = formatValue(params)
                return (
                    <Tooltip title={valueFormated} placement="bottom-start">
                        <span>
                            {valueFormated}
                        </span>
                    </Tooltip>
                )
            },
        },

    ]

    const openAlert = (message, type = "info") => {
        setAlertMessageState({
            open: true,
            message: message,
            type: type
        })
    }

    const closeAlert = () => {
        setAlertMessageState(prev => ({ ...prev, open: false }))
    }

    const generateCsvData = (body) => {

        const { PU_MTM, PUPAR, PercentualPUPAR } = body

        // assume que os arrays têm o mesmo length
        const data = PU_MTM.map((item, index) => {
            return {
                DataReferencia: item.DataReferencia,
                CETIP: item.CETIP,
                PU_MTM: formatStringToFloatDecimalCase(item.PU_MTM),
                PUPAR: formatStringToFloatDecimalCase(PUPAR[index].PUPAR),
                PercentualPUPAR: formatStringToFloatDecimalCase(PercentualPUPAR[index].PercentualPUPAR),
            }
        })

        setCsvData(data)
    }

    const loadChartData = (date) => {
        if (!selectedRowData) return

        setLoading(true)
        const cetipList = [selectedRowData.CETIP]

        priceService.generatePriceData(date.firstDate, date.endDate, cetipList)
            .then((response) => {
                setLoading(false)
                const { body } = response
                generateCsvData(body)
                const chartData = priceService.generatePUPARChart(body)

                setChart(prev => ({
                    ...prev,
                    data: chartData
                }))
            })
            .catch(() => {
                setLoading(false)
                setIsChartOpen(false)
                openAlert("Não foi possível carregar os dados, tente novamente", "warning")
            })
    }

    return (
        <div>
            <AlertMessage
                open={alertMessageState.open}
                message={alertMessageState.message}
                type={alertMessageState.type}
                close={closeAlert}
            />
            <br />
            <Grid container direction="row" justifyContent="center" spacing={2}>
                <Grid item xl={12} md={12} xs={12}>
                    <Paper sx={{ height: 'calc(120vh - 290px)', width: '100%', overflow: 'hidden' }}>
                        <DataGrid
                            rows={priceList}
                            columns={columns}
                            pageSize={25}
                            disableRowSelectionOnClick
                            sortingOrder={['asc', 'desc']}
                            localeText={localeBRDataGridText}
                        />
                    </Paper>

                </Grid>
            </Grid>

            <MultiAxisChartRangeDateModalComponent
                open={isChartOpen}
                onClose={() => setIsChartOpen(false)}
                title={`Gráfico de PUPAR - ${selectedRowData?.CETIP || ''}`}
                loadChartData={loadChartData}
                loading={loading}
                chartData={chart}
                csvData={csvData}
            />
        </div>
    )
}

PriceTablePage.propTypes = {
    date: PropTypes.string,
    setLoading: PropTypes.func,
    loading: PropTypes.bool.isRequired,
    setExportFileCsv: PropTypes.func,
}

export default PriceTablePage